import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [resetToken, setResetToken] = useState(""); // For demo only

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/password-reset-request`, { email });
      setSuccess(true);
      // For demo purposes - in production this token would be sent via email
      if (response.data.demo_token) {
        setResetToken(response.data.demo_token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#7C2C3B] via-[#63232F] to-[#4A1A23] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <Link to="/login" className="flex items-center gap-2 text-slate-500 hover:text-[#7C2C3B] mb-6 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#7C2C3B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-[#7C2C3B]" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
              FORGOT PASSWORD?
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-[#0F172A] mb-2">Check Your Email</h2>
              <p className="text-slate-500 text-sm mb-6">
                If an account exists for {email}, you'll receive a password reset link.
              </p>
              
              {/* Demo token display - remove in production */}
              {resetToken && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-xs text-amber-800 font-medium mb-2">Demo Mode - Reset Link:</p>
                  <Link 
                    to={`/reset-password?token=${resetToken}`}
                    className="text-sm text-[#7C2C3B] hover:underline break-all"
                  >
                    Click here to reset password
                  </Link>
                </div>
              )}

              <Link to="/login">
                <Button className="w-full bg-[#7C2C3B] hover:bg-[#63232F]">
                  Return to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#7C2C3B] hover:bg-[#63232F]"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-500">
              Remember your password?{" "}
              <Link to="/login" className="text-[#7C2C3B] hover:underline font-medium">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
